import { useRef, useState } from "react";
import { Play, RotateCcw } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";

const DEMOS = [
  { title: "Real-Time AI Videochat", img: "/assets/posters/tech-videochat.jpg", video: "/assets/videos/real-time-ai-videochat.mp4", pos: "50% 50%" },
  { title: "Photorealistic AI Characters", img: "/assets/posters/tech-characters.jpg", video: "/assets/videos/photorealistic-ai-characters.mp4", pos: "50% 50%" },
  { title: "Voice Interaction", img: "/assets/posters/tech-voice.jpg", video: "/assets/videos/voice-interaction.mp4", pos: "50% 50%" },
  { title: "Emotional Expression", img: "/assets/posters/tech-expression.jpg", video: "/assets/videos/emotional-expression.mp4", pos: "50% 50%" },
  { title: "Natural Movement", img: "/assets/posters/tech-movement.jpg", video: "/assets/videos/natural-movement.mp4", pos: "50% 50%" },
];

function DemoCard({ title, img, video, pos, index }) {
  const videoRef = useRef(null);
  const [state, setState] = useState("poster"); // poster | playing | ended

  const start = () => {
    const v = videoRef.current;
    if (!v) return;
    const result = v.play();
    if (result) result.catch(() => setState("poster"));
  };

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    start();
  };

  const playing = state === "playing";
  const ended = state === "ended";

  return (
    <div data-testid={`demo-card-${index}`} className="group relative overflow-hidden rounded-xl border border-white/10">
      <img
        src={img}
        alt={`${title} — concept demo poster`}
        style={{ objectPosition: pos }}
        className={`aspect-video w-full object-cover transition-all duration-700 ${
          playing ? "opacity-0" : "opacity-40 group-hover:scale-[1.04] group-hover:opacity-55"
        }`}
      />
      <video
        ref={videoRef}
        data-testid={`demo-video-${index}`}
        src={video}
        poster={img}
        preload="metadata"
        playsInline
        controls={playing}
        onPlay={() => setState("playing")}
        onEnded={() => setState("ended")}
        onError={() => setState("poster")}
        style={{ objectPosition: pos }}
        className={`absolute inset-0 h-full w-full bg-black object-contain transition-opacity duration-500 ${
          state === "poster" ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      />

      <div className="pointer-events-none absolute inset-x-0 top-0 p-5">
        <span className="w-fit rounded-full border border-cyan-300/30 bg-black/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200 backdrop-blur-md">
          Concept / Development Demo
        </span>
      </div>

      <div
        className={`absolute inset-x-0 bottom-0 flex items-end justify-between p-5 transition-opacity duration-300 ${
          playing ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <div>
          <p className="font-display text-base font-medium tracking-tight text-white">{title}</p>
        </div>
        {ended ? (
          <button
            data-testid={`demo-replay-${index}`}
            onClick={replay}
            aria-label={`Replay ${title}`}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-cyan-300/50 bg-black/60 text-cyan-200 backdrop-blur-md transition-colors duration-300 hover:bg-cyan-300/20"
          >
            <RotateCcw size={15} />
          </button>
        ) : (
          <button
            data-testid={`demo-play-${index}`}
            onClick={start}
            aria-label={`Play ${title}`}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-cyan-300/25 bg-black/60 text-cyan-100/90 backdrop-blur-md transition-colors duration-300 hover:border-cyan-300/60 hover:text-cyan-200"
          >
            <Play size={15} className="ml-0.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function TechDemo() {
  return (
    <section data-testid="tech-demo-section" className="relative bg-[#0a0a0a] py-16 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag>Technology Demo</SectionTag>
          <h2 className="font-display mt-5 text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Technology <span className="text-cyan-300">Demo</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#C5CBD5]">
            Explore individual concept demonstrations of the core technologies being developed for
            the AIROBOTCAMSTUDIO Level 1 experience.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {DEMOS.map(({ title, img, video, pos }, i) => (
            <Reveal key={title} delay={0.06 * i} className={i === 0 ? "sm:col-span-2 lg:col-span-2" : ""}>
              <DemoCard title={title} img={img} video={video} pos={pos} index={i} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p data-testid="demo-disclosure" className="mt-10 text-xs leading-relaxed text-white/55">
            Pre-rendered concept demonstration. Real-time MVP currently in development.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
